import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase, invokeFunction } from "@/integrations/supabase/client";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Bot, FileText, MessageSquare, Sparkles } from "lucide-react";
import { CoachSuggestionCard, type CoachSuggestion } from "@/components/CoachSuggestionCard";
import InsightReportView from "@/components/InsightReportView";

interface CoachPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courseId: string;
  courseTitle?: string;
  /** Optional: deep-link to a specific node when opened from LearnNodeContent */
  nodeId?: string | null;
  /** Refetch suggestions when panel opens */
  onSuggestionsRefetch?: () => void;
}

interface ChatMessage {
  id?: string;
  role: "user" | "assistant";
  content: string;
  created_at?: string;
}

export default function CoachPanel({
  open,
  onOpenChange,
  courseId,
  courseTitle = "Course",
  nodeId,
  onSuggestionsRefetch,
}: CoachPanelProps) {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState<CoachSuggestion[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const fetchSuggestions = async () => {
    const { data } = await supabase
      .from("coach_suggestions")
      .select("id, suggestion_type, message, cta_label, cta_url, priority, node_id, course_id")
      .eq("course_id", courseId)
      .eq("is_dismissed", false)
      .order("priority", { ascending: false })
      .order("generated_at", { ascending: false })
      .limit(10);
    setSuggestions((data || []) as CoachSuggestion[]);
  };

  const fetchMessages = async () => {
    const { data: conv } = await supabase
      .from("coach_conversations")
      .select("id")
      .eq("course_id", courseId)
      .maybeSingle();

    if (!conv) return;

    setConversationId(conv.id);
    const { data } = await supabase
      .from("coach_messages")
      .select("id, role, content, created_at")
      .eq("conversation_id", conv.id)
      .in("role", ["user", "assistant"])
      .order("created_at", { ascending: true });
    setMessages((data || []).map((m) => ({ role: m.role as "user" | "assistant", content: m.content, id: m.id, created_at: m.created_at })));
  };

  useEffect(() => {
    if (open && courseId) {
      fetchSuggestions();
      fetchMessages();
      onSuggestionsRefetch?.();
    }
  }, [open, courseId, onSuggestionsRefetch]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setLoading(true);

    const { data, error } = await invokeFunction("coach-chat", {
      body: { courseId, message: text, conversationId },
    });

    setLoading(false);
    if (error) {
      setMessages((prev) => [...prev, { role: "assistant", content: "Sorry, I couldn't process that. Please try again." }]);
      return;
    }

    if (data?.conversationId) setConversationId(data.conversationId);
    if (data?.message) {
      setMessages((prev) => [...prev, { role: "assistant", content: data.message }]);
    }
  };

  const handleDismiss = async (id: string) => {
    await supabase
      .from("coach_suggestions")
      .update({ is_dismissed: true, dismissed_at: new Date().toISOString() })
      .eq("id", id);
    setSuggestions((prev) => prev.filter((s) => s.id !== id));
  };

  const handleAct = (id: string, ctaUrl?: string | null) => {
    supabase
      .from("coach_suggestions")
      .update({ is_acted_upon: true, acted_upon_at: new Date().toISOString() })
      .eq("id", id);
    setSuggestions((prev) => prev.filter((s) => s.id !== id));
    if (ctaUrl && !ctaUrl.startsWith("http")) {
      onOpenChange(false);
      navigate(ctaUrl);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-2">
          <SheetTitle className="flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            AI Coach
          </SheetTitle>
          <SheetDescription>
            Get personalized help for {courseTitle}
          </SheetDescription>
        </SheetHeader>

        <Tabs defaultValue="chat" className="flex-1 flex flex-col min-h-0">
          <TabsList className="mx-6 grid w-full grid-cols-2">
            <TabsTrigger value="chat" className="gap-1.5">
              <MessageSquare className="h-4 w-4" />
              Chat
            </TabsTrigger>
            <TabsTrigger value="suggestions" className="gap-1.5">
              <Sparkles className="h-4 w-4" />
              Suggestions
            </TabsTrigger>
          </TabsList>

          <TabsContent value="chat" className="flex-1 flex flex-col min-h-0 mt-0">
            <ScrollArea className="flex-1 px-6 py-4 min-h-[200px]" style={{ maxHeight: "calc(100vh - 280px)" }}>
              <div className="space-y-4">
                {messages.length === 0 && !loading && (
                  <div className="text-sm text-muted-foreground py-8 text-center">
                    <p className="mb-2">Ask me anything about your progress.</p>
                    <p className="text-xs">Try: &quot;What should I study next?&quot; or &quot;How am I doing?&quot;</p>
                  </div>
                )}
                {messages.map((m, i) => (
                  <div
                    key={i}
                    className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[85%] rounded-xl px-4 py-2.5 text-sm ${
                        m.role === "user"
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted"
                      }`}
                    >
                      {m.role === "assistant" && m.content.includes("/learn/") ? (
                        <p className="whitespace-pre-wrap">
                          {m.content.split(/(\/learn\/courses\/[^)\s]+)/g).map((part, j) =>
                            part.startsWith("/learn/") ? (
                              <button
                                key={j}
                                type="button"
                                className="text-primary underline hover:no-underline"
                                onClick={() => {
                                  onOpenChange(false);
                                  navigate(part);
                                }}
                              >
                                {part}
                              </button>
                            ) : (
                              part
                            )
                          )}
                        </p>
                      ) : (
                        <p className="whitespace-pre-wrap">{m.content}</p>
                      )}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="rounded-xl px-4 py-2.5 bg-muted text-sm text-muted-foreground">
                      Thinking…
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
            </ScrollArea>
            <div className="p-4 border-t space-y-3">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleSend();
                }}
                className="flex gap-2"
              >
                <Textarea
                  placeholder="Ask your coach..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  className="min-h-[44px] max-h-32 resize-none"
                  rows={1}
                  disabled={loading}
                />
                <Button type="submit" size="icon" className="shrink-0 h-11 w-11" disabled={loading || !input.trim()}>
                  <MessageSquare className="h-4 w-4" />
                </Button>
              </form>
              <Button variant="outline" size="sm" className="w-full gap-2" onClick={() => setReportOpen(true)}>
                <FileText className="h-4 w-4" />
                Generate Report
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="suggestions" className="flex-1 overflow-auto mt-0">
            <div className="p-6 space-y-3">
              {suggestions.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No suggestions right now. Keep learning and check back!
                </p>
              ) : (
                suggestions.map((s) => (
                  <CoachSuggestionCard
                    key={s.id}
                    suggestion={s}
                    onDismiss={handleDismiss}
                    onAct={(id) => handleAct(id, s.cta_url)}
                  />
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>

        <InsightReportView
          open={reportOpen}
          onOpenChange={setReportOpen}
          courseId={courseId}
          courseTitle={courseTitle}
        />
      </SheetContent>
    </Sheet>
  );
}
